// src/utils/fieldDisplay.ts
//
// Display labels for a ticket's structured_fields panel (Story 4.2 AC 3). The
// canonical four (brand/region/channel/payer) render top-level; the per-type
// `extras` keys (3.6 Decision 9) render beneath. This is a LOCAL display-label
// map, NOT a re-import of the agent's fieldSchemas module into the browser build
// (AD-11 keeps supabase/functions/ out of the Vite graph). The AD-14
// single-source rule is enforced instead by a Vitest PARITY TEST
// (fieldDisplay.test.ts) that imports the import-clean fieldSchemas.ts on the
// Node side and asserts this map covers EXACTLY the canonical keys + every
// per-type extras key — drift fails CI without a risky cross-root runtime
// import. 4.5 revisits full derivation for its field editors.

import type { YakRuleLayer } from '../types/yak'

// The canonical four routing attributes render under these labels. NOTE the key
// is `region` (3.4/AD-14's matcher vocabulary), NOT `region_id`: the panel
// resolves structured_fields.region_id to a name and renders it in the `region`
// row. The asymmetry is 3.4's recorded contract.
const CANONICAL_LABELS: Record<string, string> = {
  brand: 'Brand',
  region: 'Region',
  channel: 'Channel',
  payer: 'Payer',
}

// Per-type `extras` key labels (3.6 Decision 9 interiors). Every extras key
// across all types is covered — the parity test asserts this exactly.
const EXTRAS_LABELS: Record<string, string> = {
  prescriber: 'Prescriber',
  denial_reason: 'Denial reason',
  pa_submitted: 'PA / step docs submitted',
  pharmacy_name: 'Pharmacy',
  wholesaler: 'Wholesaler',
  npi: 'NPI',
  pharmacy_phone: 'Pharmacy phone',
}

// The full label map, exported so the parity test can assert exact key coverage.
export const FIELD_LABELS: Record<string, string> = {
  ...CANONICAL_LABELS,
  ...EXTRAS_LABELS,
}

// Humanize an unknown key as a graceful fallback (snake_case → Title Case) —
// never crash or show a raw key the map hasn't seen (forward-compatible with a
// future extras key before this map is updated).
export function humanizeFieldKey(key: string): string {
  const cleaned = key.trim().replace(/_/g, ' ')
  if (!cleaned) return key
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

// The label for a field key (canonical or extras), with a humanized fallback.
export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? humanizeFieldKey(key)
}

// Map a wildcard attribute (3.4's matcher vocabulary: brand|region|channel|
// payer — `region`, not `region_id`) onto the panel's row key. Identity for the
// four canonical attributes; kept as a named helper so the panel reads clearly
// and the 3.4 asymmetry stays documented in one place.
export function wildcardFieldKey(attr: string): string {
  return attr === 'region_id' ? 'region' : attr
}

// Resolve an assignment row's layer_id against the rule's layers jsonb to a
// display: the layer's position label + its 1-based index. layer_id has no FK
// (it lives inside the rule jsonb) and a rule edit can drop a layer, so this is
// NULL-TOLERANT: a null id or a since-edited-away layer resolves to null and the
// timeline renders "—".
export interface LayerDisplay {
  position: string
  index: number
}

export function resolveLayerDisplay(
  layers: YakRuleLayer[] | null | undefined,
  layerId: string | null | undefined
): LayerDisplay | null {
  if (!layers || !layerId) return null
  const idx = layers.findIndex(l => l.layer_id === layerId)
  if (idx === -1) return null
  return { position: layers[idx].position, index: idx + 1 }
}
